/**
 * Data → Stores for an organisation with reps (Blue Mango, 2026-10-16 — Helena: "put it under the Data tab, like we
 * have Stores under Spear, with an extra column for store address and REP name"). Every store on the organisation's
 * rep list (Dis-Chem, Checkers, MediRite, Clicks): code, name, town, ADDRESS (the store's map point) and the REP
 * (dropdown, "No rep" = off every route). Add store / Add rep. Each change re-plans the reps involved from tomorrow.
 * Reps (Add rep, homes) live on Data → Reps. Same server actions as Rep Routing's Stores & reps tab (/field/stores, store-rep, store-add, store-address, rep-add);
 * the org admin (Linda) and masters edit, a CAM (Monique) views.
 */
import { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import {
  Box, Button, Flex, HStack, Input, Select, Text, Badge, Spinner, Table, Tbody, Td, Th, Thead, Tr, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, 
} from "@chakra-ui/react"
import { FaMapMarkerAlt, FaStore } from "react-icons/fa"
import PageToolbar from "../../../components/shared/PageToolbar"
import { Panel } from "../../../components/shared/Section"
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext"
import { neutral, type } from "../../../theme/tokens"

const API = process.env.REACT_APP_API_URL
const uid = () => localStorage.getItem("userid")
const RET = { DIS: "Dis-Chem", CHK: "Checkers", CLI: "Clicks" }
// a store's state (2026-10-16, Helena): its rep, No rep assigned, Not Blue Mango, or Select rep (not decided yet —
// also every store with sales that is not on the list). "No rep" (the count Rep Sales' Not assigned matches) = Select rep + No rep assigned.
const NO_REP = "__NO_REP__", NOT_BM = "__NOT_BM__"
const choiceOf = (s) => (s.status === "no_rep" ? NO_REP : s.status === "not_bm" ? NOT_BM : s.rep || "")
// "No rep assigned" = only the stores Linda marked so; every undecided store stays under "Select rep" (Helena 2026-10-16)
const isNoRep = (s) => s.status === "no_rep"
const retCode = (s) => (s.retailer === "CHK" && /medirite/i.test(s.site_description || "") ? "MED" : s.retailer)
const retName = (s) => (s.retailer === "CHK" && /medirite/i.test(s.site_description || "") ? "MediRite" : RET[s.retailer] || s.retailer)

const RepStores = () => {
  const { selectedOrg, isMaster } = useSupplierRetailer()
  const toast = useToast()
  const orgArg = useMemo(() => (isMaster ? { org_code: selectedOrg } : {}), [isMaster, selectedOrg])
  const post = useCallback((path, body = {}) => axios.post(`${API}/field/${path}`, { userid: uid(), ...orgArg, ...body }), [orgArg])

  const [list, setList] = useState(null)
  const [reps, setReps] = useState([])
  const [canEdit, setCanEdit] = useState(false)
  const [error, setError] = useState(null)
  const [q, setQ] = useState("")
  const [ret, setRet] = useState("") // "" = all retailers
  const [filter, setFilter] = useState("all") // all | norep | nomap
  const [saving, setSaving] = useState(null)
  // 100 rows at a time — 1,600 rows each with a rep dropdown made the page slow to open (Helena 2026-10-16)
  const PAGE = 100
  const [shown, setShown] = useState(PAGE)
  useEffect(() => { setShown(PAGE) }, [q, ret, filter])
  const [busy, setBusy] = useState(false)
  const [addrEdit, setAddrEdit] = useState(null)
  const [addStore, setAddStore] = useState(null)

  const load = useCallback(() => {
    setList(null); setError(null)
    post("stores")
      .then((r) => { setList(r.data.data || []); setReps(r.data.reps || []); setCanEdit(!!r.data.canEdit) })
      .catch((e) => setError(e?.response?.data?.message || e.message))
  }, [post])
  useEffect(load, [load])

  const changeRep = async (s, rep) => {
    const key = `${s.retailer}|${s.site_code}`
    setSaving(key)
    try {
      const r = await post("store-rep", { site_code: s.site_code, retailer: s.retailer, rep, site_description: s.site_description })
      const st = r.data?.data?.store_status || (rep ? "rep" : "select")
      const realRep = rep === NO_REP || rep === NOT_BM ? null : rep || null
      setList((l) => l.map((x) => (`${x.retailer}|${x.site_code}` === key ? { ...x, rep: realRep, status: st, onList: true } : x)))
      const said = rep === NO_REP ? "No rep assigned" : rep === NOT_BM ? "Not Blue Mango" : rep || "Select rep"
      toast({ title: `${s.site_description} → ${said}`, description: realRep || s.rep ? "Routes re-planned from tomorrow" : undefined, status: "success", duration: 2500, isClosable: true })
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setSaving(null) }
  }
  const saveAddress = async () => {
    setBusy(true)
    try {
      const body = { site_code: addrEdit.site_code, retailer: addrEdit.retailer, address: addrEdit.address }
      if (addrEdit.lat !== "" && addrEdit.lng !== "") { body.lat = Number(addrEdit.lat); body.lng = Number(addrEdit.lng) }
      await post("store-address", body)
      toast({ title: "Address saved", description: "The store is placed on the map; its rep is re-planned from tomorrow", status: "success", isClosable: true })
      setAddrEdit(null); load()
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setBusy(false) }
  }
  const saveAddStore = async () => {
    setBusy(true)
    try {
      const r = await post("store-add", addStore)
      const d = r.data.data
      toast({ title: `${d.site_description} added for ${d.rep}`, description: d.hasPoint ? "Route re-planned from tomorrow" : "No map location yet — set its address so it can be routed", status: d.hasPoint ? "success" : "warning", duration: 6000, isClosable: true })
      setAddStore(null); load()
    } catch (e) {
      toast({ title: "Not added", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setBusy(false) }
  }

  if (error) return <Panel><Text fontSize={type.size.small} color={neutral[600]}>{error}</Text></Panel>

  const rows = (list || []).filter((s) => {
    if (ret && retName(s) !== ret) return false
    if (filter === "norep" && !isNoRep(s)) return false
    if (filter === "select" && s.status !== "select") return false
    if (filter === "notbm" && s.status !== "not_bm") return false
    if (filter === "nomap" && s.lat != null) return false
    const t = q.trim().toLowerCase()
    return !t || `${s.site_code} ${s.site_description} ${s.town || ""} ${s.rep || ""} ${s.address || ""}`.toLowerCase().includes(t)
  })
  const count = (f) => (list || []).filter((s) => (!ret || retName(s) === ret) && (f === "norep" ? isNoRep(s) : f === "select" ? s.status === "select" : f === "notbm" ? s.status === "not_bm" : f === "nomap" ? s.lat == null : true)).length
  const repOptions = reps.length ? reps : []

  return (
    <Box>
      <PageToolbar
        description={`Every store on the rep lists and every store with sales — address and rep${canEdit ? " (change a rep in the Rep column; every change re-plans the reps from tomorrow)" : " · view only"}`}
        actions={canEdit ? (
          <HStack>
            <Button size="sm" colorScheme="teal" variant="outline" leftIcon={<FaStore />} onClick={() => setAddStore({ retailer: "DIS", site_code: "", site_description: "", town: "", rep: "" })}>Add store</Button>
          </HStack>
        ) : null}
      />
      <Panel>
        <Flex gap={2} mb={3} wrap="wrap" align="center">
          <Input size="sm" maxW="300px" placeholder="Search store, town, address or rep" value={q} onChange={(e) => setQ(e.target.value)} />
          <Select size="sm" maxW="170px" value={ret} onChange={(e) => setRet(e.target.value)}>
            <option value="">All retailers</option>
            {["Dis-Chem", "Checkers", "MediRite", "Clicks"].map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
          <Button size="sm" variant={filter === "all" ? "solid" : "outline"} colorScheme={filter === "all" ? "teal" : "gray"} onClick={() => setFilter("all")}>All stores ({count("all")})</Button>
          <Button size="sm" variant={filter === "norep" ? "solid" : "outline"} colorScheme={filter === "norep" ? "orange" : "gray"} onClick={() => setFilter("norep")}>No rep assigned ({count("norep")})</Button>
          <Button size="sm" variant={filter === "notbm" ? "solid" : "outline"} colorScheme={filter === "notbm" ? "gray" : "gray"} onClick={() => setFilter("notbm")}>Not Blue Mango ({count("notbm")})</Button>
          <Button size="sm" variant={filter === "select" ? "solid" : "outline"} colorScheme={filter === "select" ? "yellow" : "gray"} onClick={() => setFilter("select")}>Select rep ({count("select")})</Button>
          <Button size="sm" variant={filter === "nomap" ? "solid" : "outline"} colorScheme={filter === "nomap" ? "red" : "gray"} onClick={() => setFilter("nomap")}>No map location ({count("nomap")})</Button>
        </Flex>
        {!list ? <Spinner size="sm" /> : (
          <Box overflowX="auto" maxH="70vh" overflowY="auto">
            <Table size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1.5 } }}>
              <Thead bg={neutral[50]} position="sticky" top={0} zIndex={1}>
                <Tr><Th w="1%">Retailer</Th><Th w="1%">Store code</Th><Th>Store name</Th><Th w="1%">Rep</Th><Th>Town</Th><Th>Store address</Th><Th w="1%"></Th></Tr>
              </Thead>
              <Tbody>
                {rows.slice(0, shown).map((s) => (
                  <Tr key={`${s.retailer}|${s.site_code}`}>
                    <Td><Badge colorScheme={s.retailer === "CLI" ? "blue" : retCode(s) === "MED" ? "purple" : s.retailer === "CHK" ? "red" : "green"} title={retName(s)}>{retCode(s)}</Badge></Td>
                    <Td whiteSpace="nowrap">{s.site_code}</Td>
                    <Td fontWeight={600}>{s.site_description}</Td>
                    <Td minW="190px">
                      {canEdit ? (
                        <Select size="xs" value={choiceOf(s)} isDisabled={saving === `${s.retailer}|${s.site_code}`} onChange={(e) => changeRep(s, e.target.value)}
                          bg={s.status === "rep" ? "white" : s.status === "not_bm" ? "gray.100" : s.status === "no_rep" ? "orange.50" : "yellow.50"}>
                          <option value="">— Select rep —</option>
                          {repOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                          <option value={NO_REP}>No rep assigned</option>
                          <option value={NOT_BM}>Not Blue Mango</option>
                        </Select>
                      ) : (s.rep || <Badge colorScheme={s.status === "not_bm" ? "gray" : s.status === "no_rep" ? "orange" : "yellow"}>{s.status === "not_bm" ? "Not Blue Mango" : s.status === "no_rep" ? "No rep assigned" : "Select rep"}</Badge>)}
                    </Td>
                    <Td whiteSpace="nowrap">{s.town || "–"}</Td>
                    <Td maxW="420px" whiteSpace="normal">
                      {s.lat == null ? <Badge colorScheme="red">No map location</Badge> : <Text color={neutral[700]}>{s.address || <Text as="span" color={neutral[400]}>found automatically</Text>}</Text>}
                    </Td>
                    <Td>{canEdit && <Button size="xs" variant="ghost" colorScheme="teal" leftIcon={<FaMapMarkerAlt />} onClick={() => setAddrEdit({ ...s, address: s.address || "", lat: "", lng: "" })}>Edit</Button>}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {rows.length === 0 && <Text fontSize={type.size.small} color={neutral[500]} mt={2}>Nothing to show.</Text>}
            {rows.length > shown && (
              <Flex mt={3} align="center" gap={3}>
                <Button size="sm" variant="outline" onClick={() => setShown((n) => n + PAGE)}>Show {Math.min(PAGE, rows.length - shown)} more</Button>
                <Button size="sm" variant="ghost" onClick={() => setShown(rows.length)}>Show all {rows.length}</Button>
                <Text fontSize={type.size.small} color={neutral[500]}>Showing {shown} of {rows.length} — search or pick a retailer to find a store faster.</Text>
              </Flex>
            )}
          </Box>
        )}
      </Panel>

      <Modal isOpen={!!addrEdit} onClose={() => setAddrEdit(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Store address — {addrEdit?.site_description}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize={type.size.small} color={neutral[600]} mb={2}>Street address (found on the map), or the coordinates. Saving re-plans the store's rep from tomorrow.</Text>
            <Input mb={3} placeholder="e.g. Shop 12, Canal Walk, Century Boulevard, Cape Town" value={addrEdit?.address || ""} onChange={(e) => setAddrEdit({ ...addrEdit, address: e.target.value })} />
            <HStack>
              <Input placeholder="Latitude (optional)" value={addrEdit?.lat || ""} onChange={(e) => setAddrEdit({ ...addrEdit, lat: e.target.value })} />
              <Input placeholder="Longitude (optional)" value={addrEdit?.lng || ""} onChange={(e) => setAddrEdit({ ...addrEdit, lng: e.target.value })} />
            </HStack>
            {addrEdit && !addrEdit.rep && <Text fontSize={type.size.caption} color="orange.600" mt={2}>This store has no rep — give it a rep first, then set its address.</Text>}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setAddrEdit(null)}>Cancel</Button>
            <Button colorScheme="teal" onClick={saveAddress} isLoading={busy} isDisabled={!addrEdit?.rep || (!addrEdit?.address && (addrEdit?.lat === "" || addrEdit?.lng === ""))}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!addStore} onClose={() => setAddStore(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add store</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize={type.size.small} color={neutral[600]} mb={2}>The retailer's store code (Dis-Chem e.g. S217, Checkers / MediRite e.g. 0185, Clicks e.g. 384). The name is filled from the retailer's store list when the code is known.</Text>
            <HStack mb={3}>
              <Select maxW="170px" value={addStore?.retailer || "DIS"} onChange={(e) => setAddStore({ ...addStore, retailer: e.target.value })}>
                <option value="DIS">Dis-Chem</option><option value="CHK">Checkers / MediRite</option><option value="CLI">Clicks</option>
              </Select>
              <Input placeholder="Store code" value={addStore?.site_code || ""} onChange={(e) => setAddStore({ ...addStore, site_code: e.target.value })} />
            </HStack>
            <Input mb={3} placeholder="Store name (needed only for a code the retailer list does not have)" value={addStore?.site_description || ""} onChange={(e) => setAddStore({ ...addStore, site_description: e.target.value })} />
            <Input mb={3} placeholder="Town (optional)" value={addStore?.town || ""} onChange={(e) => setAddStore({ ...addStore, town: e.target.value })} />
            <Select placeholder="Rep" value={addStore?.rep || ""} onChange={(e) => setAddStore({ ...addStore, rep: e.target.value })}>
              {repOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setAddStore(null)}>Cancel</Button>
            <Button colorScheme="teal" onClick={saveAddStore} isLoading={busy} isDisabled={!addStore?.site_code || !addStore?.rep}>Add</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  )
}

export default RepStores
